import type { ApiError } from '@pipyar/core';

/**
 * Thin fetch wrapper.
 *
 * Requests are same-origin in production (nginx serves the SPA and proxies /api)
 * and same-origin in development too, because Vite proxies /api. So relative URLs
 * work everywhere and there is no base-URL configuration to get wrong.
 */

export class ApiRequestError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }
}

function isApiError(value: unknown): value is ApiError {
  return (
    typeof value === 'object' &&
    value !== null &&
    'error' in value &&
    typeof (value as ApiError).error?.code === 'string'
  );
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let response: Response;
  try {
    response = await fetch(path, {
      ...init,
      // Send and accept the session cookie.
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
        ...init?.headers,
      },
    });
  } catch {
    // A rejected fetch means the network or the API process, not an HTTP status.
    throw new ApiRequestError(
      'NETWORK',
      'Cannot reach the server. Check that the API is running.',
      0,
    );
  }

  const text = await response.text();
  const payload: unknown = text ? safeJsonParse(text) : null;

  if (!response.ok) {
    if (isApiError(payload)) {
      throw new ApiRequestError(payload.error.code, payload.error.message, response.status);
    }
    throw new ApiRequestError(
      'HTTP_ERROR',
      `Request failed with status ${response.status}.`,
      response.status,
    );
  }

  return payload as T;
}

function safeJsonParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

const withBody = (method: string) =>
  <T,>(path: string, body?: unknown): Promise<T> =>
    request<T>(path, {
      method,
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });

export const api = {
  get: <T>(path: string): Promise<T> => request<T>(path, { method: 'GET' }),
  post: withBody('POST'),
  put: withBody('PUT'),
  patch: withBody('PATCH'),
  // Named `del` because `delete` is a reserved word and cannot be a shorthand
  // property that is also callable as api.delete without quoting at every site.
  del: <T>(path: string): Promise<T> => request<T>(path, { method: 'DELETE' }),
};
